/**
 * UI adapter: bridges React SOC handlers to ExecuteSocAction use case (UC-14)
 * Enable via architectureFeatures.useSocActionUseCase
 */
import { useCallback } from 'react';
import { executeSocActionUseCase } from '../../../infrastructure/di/container';
import { architectureFeatures } from '../../../infrastructure/config/features';
import { ExecuteSocActionUseCase } from '../../../application/use-cases/ExecuteSocActionUseCase';
import type {
  ExecuteSocActionInput,
  ExecuteSocActionOutput,
  SocActionType,
} from '../../../application/dto/ExecuteSocActionDto';

export type { ExecuteSocActionInput, ExecuteSocActionOutput, SocActionType };

/** Run SOC action through clean architecture use case (UC-14) */
export function executeExecuteSocAction(
  input: ExecuteSocActionInput,
): ExecuteSocActionOutput {
  if (!architectureFeatures.useSocActionUseCase) {
    throw new Error('useSocActionUseCase feature flag is disabled');
  }
  return executeSocActionUseCase.execute(input);
}

/** Legacy fallback — pure use case without DI container */
export function executeLegacyExecuteSocAction(
  input: ExecuteSocActionInput,
): ExecuteSocActionOutput {
  return new ExecuteSocActionUseCase().execute(input);
}

/** Resolve SOC action — UC-14 when flag on, else legacy path */
export function resolveExecuteSocAction(
  input: ExecuteSocActionInput,
): ExecuteSocActionOutput {
  if (architectureFeatures.useSocActionUseCase) {
    return executeExecuteSocAction(input);
  }
  return executeLegacyExecuteSocAction(input);
}

/**
 * Hook helper for useSocHandlers integration.
 * Returns a stable callback that runs UC-14 when feature flag is on.
 */
export function useSocMutationRunner() {
  const runExecuteSocAction = useCallback(
    (input: ExecuteSocActionInput): ExecuteSocActionOutput | null => {
      if (!architectureFeatures.useSocActionUseCase) {
        return null;
      }
      return executeExecuteSocAction(input);
    },
    [],
  );

  return { runExecuteSocAction };
}